import * as React from 'react';
import {
  Camera,
  Upload,
  Sparkles,
  Check,
  Loader2,
  ImagePlus,
  X,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type UploadPhase =
  | 'idle'           // Waiting for user to drop/select files
  | 'uploading'      // Files being uploaded
  | 'analyzing'      // AI is processing the images
  | 'complete'       // AI analysis done, fields auto-filled
  | 'error';         // Something failed

interface AnalysisResult {
  suggestedTitle?: string;
  suggestedDescription?: string;
  suggestedCategory?: string;
  suggestedCondition?: string;
  suggestedPrice?: number;
  confidence: number; // 0-1
}

interface AiUploadZoneProps {
  /** Current phase of the upload+analysis pipeline */
  phase: UploadPhase;
  /** Preview URLs of uploaded images */
  previews: string[];
  /** AI analysis result (available when phase === 'complete') */
  analysis?: AnalysisResult;
  /** Error message (available when phase === 'error') */
  errorMessage?: string;
  /** Max number of photos allowed */
  maxPhotos?: number;
  /** Callback when files are selected or dropped */
  onFilesSelected: (files: File[]) => void;
  /** Callback to remove a specific preview */
  onRemovePhoto?: (index: number) => void;
  /** Callback to retry after error */
  onRetry?: () => void;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Phase-specific content blocks                                      */
/* ------------------------------------------------------------------ */

function IdleContent({ maxPhotos }: { maxPhotos: number }) {
  return (
    <div className="flex flex-col items-center gap-4 py-6">
      {/* Icon cluster — camera + sparkles to hint at AI magic */}
      <div className="relative">
        <div className="grid h-16 w-16 place-items-center rounded-xl bg-[var(--color-primary-50)] text-[var(--color-primary)] ring-1 ring-[var(--color-primary-100)]">
          <Camera className="h-7 w-7" />
        </div>
        <div className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full bg-[var(--color-accent)] text-[var(--color-paper)] shadow-sm">
          <Sparkles className="h-3.5 w-3.5" />
        </div>
      </div>

      <div className="space-y-1 text-center">
        <p className="text-sm font-semibold text-[var(--color-ink)]">
          Sube fotos y la IA completa el aviso
        </p>
        <p className="text-[0.8125rem] leading-relaxed text-[var(--color-ink-muted)]">
          Arrastra imagenes aqui, o{' '}
          <span className="font-medium text-[var(--color-primary)] underline underline-offset-2">
            selecciona archivos
          </span>
          . Tambien puedes{' '}
          <span className="font-medium text-[var(--color-primary)] underline underline-offset-2">
            usar la camara
          </span>
          .
        </p>
        <p className="text-[0.6875rem] text-[var(--color-ink-faint)]">
          JPG o PNG, max {maxPhotos} fotos, 10 MB cada una
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="primary" size="sm" type="button">
          <ImagePlus className="h-4 w-4" />
          Seleccionar fotos
        </Button>
        <Button variant="outline" size="sm" type="button">
          <Camera className="h-4 w-4" />
          Camara
        </Button>
      </div>
    </div>
  );
}

function UploadingContent() {
  return (
    <div className="flex flex-col items-center gap-3 py-8">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-[var(--color-primary-50)]">
        <Upload className="h-5 w-5 animate-bounce text-[var(--color-primary)]" />
      </div>
      <p className="text-sm font-medium text-[var(--color-ink)]">
        Subiendo imagenes...
      </p>
      {/* Progress bar */}
      <div className="h-1 w-48 overflow-hidden rounded-full bg-[var(--color-paper-deep)]">
        <div
          className="h-full rounded-full bg-[var(--color-primary)] transition-all duration-500"
          style={{ width: '60%', animation: 'indeterminate 1.4s ease-in-out infinite' }}
        />
      </div>
    </div>
  );
}

function AnalyzingContent() {
  return (
    <div className="flex flex-col items-center gap-3 py-8">
      {/* Pulsing sparkle ring */}
      <div className="relative grid h-14 w-14 place-items-center">
        <div className="absolute inset-0 rounded-full bg-[var(--color-accent-50)] animate-ping opacity-30" />
        <div className="relative grid h-14 w-14 place-items-center rounded-full bg-[var(--color-accent-50)] ring-1 ring-[var(--color-accent)]/20">
          <Sparkles className="h-6 w-6 text-[var(--color-accent)]" />
        </div>
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-[var(--color-ink)]">
          Analizando con IA...
        </p>
        <p className="mt-0.5 text-[0.8125rem] text-[var(--color-ink-muted)]">
          Identificando producto, categoria y estado
        </p>
      </div>
      {/* Animated field reveal placeholders */}
      <div className="mt-2 w-full max-w-xs space-y-2">
        {['Titulo', 'Categoria', 'Precio sugerido'].map((label, i) => (
          <div
            key={label}
            className="stagger-fade flex items-center gap-2 rounded border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] px-3 py-2"
            style={{ animationDelay: `${(i + 1) * 400}ms` }}
          >
            <Loader2 className="h-3 w-3 animate-spin text-[var(--color-accent)]" />
            <span className="text-xs text-[var(--color-ink-faint)]">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompleteContent({ analysis }: { analysis?: AnalysisResult }) {
  const fields = [
    { label: 'Titulo', value: analysis?.suggestedTitle },
    { label: 'Categoria', value: analysis?.suggestedCategory },
    {
      label: 'Precio sugerido',
      value: analysis?.suggestedPrice
        ? `$${analysis.suggestedPrice.toLocaleString('es-CL')}`
        : undefined,
    },
    { label: 'Condicion', value: analysis?.suggestedCondition },
  ].filter((f) => f.value);

  return (
    <div className="flex flex-col items-center gap-3 py-6">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-[var(--color-moss-50)] ring-1 ring-[var(--color-moss)]/20">
        <Check className="h-5 w-5 text-[var(--color-moss)]" />
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-[var(--color-ink)]">
          Analisis completado
        </p>
        {analysis && (
          <p className="mt-0.5 text-[0.6875rem] text-[var(--color-ink-faint)]">
            Confianza: {Math.round(analysis.confidence * 100)}%
          </p>
        )}
      </div>
      {/* Progressive reveal of detected fields */}
      <div className="w-full max-w-xs space-y-1.5">
        {fields.map((f, i) => (
          <div
            key={f.label}
            className="stagger-fade flex items-center justify-between rounded border border-[var(--color-moss)]/20 bg-[var(--color-moss-50)] px-3 py-2"
            style={{ animationDelay: `${i * 120}ms` }}
          >
            <span className="text-[0.6875rem] font-medium text-[var(--color-ink-muted)]">
              {f.label}
            </span>
            <span className="text-xs font-semibold text-[var(--color-ink)]">
              {f.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorContent({
  message,
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-8">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-[var(--color-danger-50)] ring-1 ring-[var(--color-danger)]/20">
        <X className="h-5 w-5 text-[var(--color-danger)]" />
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-[var(--color-ink)]">
          No se pudo analizar
        </p>
        <p className="mt-0.5 text-[0.8125rem] text-[var(--color-ink-muted)]">
          {message ?? 'Ocurrio un error. Intenta de nuevo.'}
        </p>
      </div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} type="button">
          <RotateCcw className="h-4 w-4" />
          Reintentar
        </Button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Photo previews strip                                               */
/* ------------------------------------------------------------------ */

function PhotoStrip({
  previews,
  onRemove,
}: {
  previews: string[];
  onRemove?: (i: number) => void;
}) {
  if (previews.length === 0) return null;
  return (
    <div className="flex gap-2 overflow-x-auto px-4 pb-3 pt-1 scrollbar-thin">
      {previews.map((url, i) => (
        <div
          key={url}
          className="relative h-16 w-16 shrink-0 overflow-hidden rounded border border-[var(--color-hairline)]"
        >
          <img
            src={url}
            alt={`Foto ${i + 1}`}
            className="h-full w-full object-cover"
          />
          {onRemove && (
            <button
              type="button"
              aria-label={`Eliminar foto ${i + 1}`}
              onClick={() => onRemove(i)}
              className="absolute right-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-full bg-[var(--color-ink)]/70 text-[var(--color-paper)] backdrop-blur-sm transition-colors hover:bg-[var(--color-danger)]"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

/**
 * AiUploadZone
 * ------------
 * Drag-and-drop / camera capture area for marketplace listing photos.
 * Orchestrates a multi-phase flow:
 *   idle -> uploading -> analyzing -> complete
 *
 * Visual treatment:
 * - Dashed border (idle) transitions to solid on drag-over
 * - paper-dim background with subtle grain texture
 * - Phase transitions animate with stagger-fade
 * - Accent sparkle motif signals AI involvement
 * - Photo thumbnails appear as a horizontal strip below the drop zone
 *
 * The component is a controlled UI shell; actual file handling,
 * upload, and AI calls live in the parent or a custom hook.
 */
export function AiUploadZone({
  phase,
  previews,
  analysis,
  errorMessage,
  maxPhotos = 6,
  onFilesSelected,
  onRemovePhoto,
  onRetry,
  className,
}: AiUploadZoneProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);

  /* -- Drop handlers -- */
  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith('image/'),
    );
    if (files.length > 0) onFilesSelected(files);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) onFilesSelected(files);
    // Reset so the same file can be re-selected
    e.target.value = '';
  }

  const isInteractive = phase === 'idle' || phase === 'complete' || phase === 'error';

  return (
    <div className={cn('space-y-0', className)}>
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="sr-only"
        onChange={handleFileChange}
        aria-label="Seleccionar fotos del producto"
      />

      {/* Drop zone */}
      <div
        role="button"
        tabIndex={isInteractive ? 0 : -1}
        aria-label="Zona de carga de fotos"
        onClick={() => isInteractive && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative overflow-hidden rounded-md transition-all duration-200',
          // Dashed border in idle, solid in other states
          phase === 'idle'
            ? 'border-2 border-dashed'
            : 'border border-solid',
          // Background
          'bg-[var(--color-paper-dim)]',
          // Border color
          isDragOver
            ? 'border-[var(--color-primary)] bg-[var(--color-primary-50)]'
            : phase === 'complete'
              ? 'border-[var(--color-moss)]/30'
              : phase === 'error'
                ? 'border-[var(--color-danger)]/30'
                : 'border-[var(--color-hairline-strong)]',
          // Cursor
          isInteractive ? 'cursor-pointer' : 'cursor-default',
          // Hover
          isInteractive &&
            'hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-50)]/50',
        )}
      >
        {phase === 'idle' && <IdleContent maxPhotos={maxPhotos} />}
        {phase === 'uploading' && <UploadingContent />}
        {phase === 'analyzing' && <AnalyzingContent />}
        {phase === 'complete' && <CompleteContent analysis={analysis} />}
        {phase === 'error' && (
          <ErrorContent message={errorMessage} onRetry={onRetry} />
        )}
      </div>

      {/* Photo previews strip */}
      {previews.length > 0 && (
        <div className="mt-3 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] py-2">
          <p className="px-4 pb-2 text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-[var(--color-ink-faint)]">
            Fotos ({previews.length}/{maxPhotos})
          </p>
          <PhotoStrip previews={previews} onRemove={onRemovePhoto} />
        </div>
      )}
    </div>
  );
}
